import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "ui";

import { CloudflareTurnstile } from "src/components/CloudflareTurnstile";
import {
  QuestionnaireForm,
  QUESTIONNAIRE_FORM_ID,
} from "src/components/QuestionnaireForm";
import { env } from "src/env";
import { usePublicProfileContactForm } from "src/hooks/usePublicProfileContactForm";
import { type ProfileContactFormQuestionnaire } from "src/profile/profileContactForm";

interface PublicProfileContactFormProps {
  buttonText: string;
  buttonColor: string;
  buttonTextColor: string;
  questionnaire: ProfileContactFormQuestionnaire | null;
  slug: string;
  isPreview: boolean;
}

export function PublicProfileContactForm(props: PublicProfileContactFormProps) {
  const translation = useTranslation();
  const t = translation.t;
  const turnstileSiteKey = env.VITE_APP_TURNSTILE_SITE_KEY;
  const consentLocale =
    translation.i18n.resolvedLanguage === "pl" ? "pl" : "en";
  const contactForm = usePublicProfileContactForm({
    slug: props.slug,
    isPreview: props.isPreview,
    turnstileSiteKey,
    consentLocale,
    requiresHealthDataConsent: props.questionnaire !== null,
  });
  const { state } = contactForm;

  return (
    <>
      <Button
        type="button"
        className="fixed left-1/2 z-20 h-10 w-max max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-full px-4 text-sm shadow-lg ring-1 ring-black/5 hover:opacity-95"
        style={{
          bottom: "max(1.5rem, env(safe-area-inset-bottom))",
          backgroundColor: props.buttonColor,
          color: props.buttonTextColor,
        }}
        onClick={() => {
          contactForm.handleOpenChange(true);
        }}
      >
        <Icon name="tabler-message-circle" className="size-4 shrink-0" />
        <span className="truncate">{props.buttonText}</span>
      </Button>

      <Dialog open={state.open} onOpenChange={contactForm.handleOpenChange}>
        <DialogContent className="flex max-h-[min(90vh,48rem)] max-w-xl flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>
              {props.questionnaire?.name ?? t("profileContactFormDefaultTitle")}
            </DialogTitle>
            <DialogDescription>
              {props.questionnaire?.description ||
                t("profileContactFormModalDescription")}
            </DialogDescription>
          </DialogHeader>

          {state.submitted ? (
            <>
              <div className="flex flex-col items-center px-6 py-10 text-center">
                <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <Icon name="tabler-check" className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">
                  {t("profileContactFormSuccessTitle")}
                </h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  {t("profileContactFormSuccessDescription")}
                </p>
              </div>
              <DialogFooter className="border-t px-6 py-4">
                <Button
                  type="button"
                  onClick={() => {
                    contactForm.handleOpenChange(false);
                  }}
                >
                  {t("close")}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="overflow-y-auto px-4 py-2">
                <QuestionnaireForm
                  key={props.questionnaire?.id ?? "default-contact-form"}
                  questionnaire={props.questionnaire}
                  onSubmit={contactForm.handleSubmit}
                  isDisabled={contactForm.isPending}
                  beforeItems={
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="profile-contact-name">
                          {t("profileContactFormName")}
                          <span className="ml-1 leading-none text-red-500">
                            *
                          </span>
                        </Label>
                        <Input
                          id="profile-contact-name"
                          name="name"
                          autoComplete="name"
                          required
                          maxLength={100}
                          value={state.name}
                          placeholder={t("profileContactFormNamePlaceholder")}
                          disabled={contactForm.isPending}
                          onChange={(event) => {
                            contactForm.setField("name", event.target.value);
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profile-contact-email">
                          {t("profileContactFormEmail")}
                          <span className="ml-1 leading-none text-red-500">
                            *
                          </span>
                        </Label>
                        <Input
                          id="profile-contact-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={state.email}
                          placeholder={t("profileContactFormEmailPlaceholder")}
                          disabled={contactForm.isPending}
                          onChange={(event) => {
                            contactForm.setField("email", event.target.value);
                          }}
                        />
                      </div>
                      <div
                        className="absolute -left-[10000px] h-px w-px overflow-hidden"
                        aria-hidden="true"
                      >
                        <Label htmlFor="profile-contact-website">Website</Label>
                        <Input
                          id="profile-contact-website"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                          value={state.website}
                          onChange={(event) => {
                            contactForm.setField("website", event.target.value);
                          }}
                        />
                      </div>
                    </>
                  }
                  afterItems={
                    <>
                      {props.questionnaire ? (
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="profile-contact-health-data-consent"
                            checked={state.healthDataConsent}
                            disabled={contactForm.isPending}
                            aria-invalid={state.healthDataConsentError}
                            aria-required="true"
                            aria-describedby={
                              state.healthDataConsentError
                                ? "profile-contact-health-data-consent-error"
                                : undefined
                            }
                            onCheckedChange={(checked) => {
                              const isChecked = checked === true;
                              contactForm.setField(
                                "healthDataConsent",
                                isChecked,
                              );

                              if (isChecked) {
                                contactForm.setField(
                                  "healthDataConsentError",
                                  false,
                                );
                              }
                            }}
                          />
                          <div className="space-y-1">
                            <Label
                              htmlFor="profile-contact-health-data-consent"
                              className="text-xs leading-relaxed font-normal"
                            >
                              {t("profileContactFormHealthDataConsent")}{" "}
                              <a
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2"
                              >
                                {t("profileContactFormPrivacyPolicy")}
                              </a>
                              .
                            </Label>
                            {state.healthDataConsentError ? (
                              <p
                                id="profile-contact-health-data-consent-error"
                                className="text-xs text-destructive"
                                role="alert"
                              >
                                {t(
                                  "profileContactFormHealthDataConsentRequired",
                                )}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {!props.isPreview ? (
                        <CloudflareTurnstile
                          siteKey={turnstileSiteKey}
                          action="turnstile-spin-v2"
                          resetKey={state.turnstileResetKey}
                          onTokenChange={(token) => {
                            contactForm.setField("turnstileToken", token);
                          }}
                        />
                      ) : null}
                      {state.submissionError ? (
                        <p className="text-sm text-destructive" role="alert">
                          {t("profileContactFormSubmissionError")}
                        </p>
                      ) : null}
                    </>
                  }
                />
              </div>
              <DialogFooter className="border-t px-6 py-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={contactForm.isPending}
                  onClick={() => {
                    contactForm.handleOpenChange(false);
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  form={QUESTIONNAIRE_FORM_ID}
                  disabled={
                    contactForm.isPending ||
                    (!props.isPreview &&
                      Boolean(turnstileSiteKey) &&
                      !state.turnstileToken)
                  }
                >
                  {contactForm.isPending ? (
                    <Icon
                      name="tabler-loader-2"
                      className="size-4 animate-spin"
                    />
                  ) : null}
                  {t("profileContactFormSubmit")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
